import client from "shared/utils/axios-config";
import { now, tzDate } from "shared/utils/dayjs";
import { AnalyticsRange, SalesTrendMonths } from "shared/types";

// Downloads an analytics report workbook through the authed axios client and
// hands it to the browser as a file. The server names the file via
// Content-Disposition (exposed through CORS); the local fallback only covers
// a misconfigured proxy stripping the header.
export const downloadAnalyticsExport = async (
  params: Record<string, string | number | undefined>,
  fallbackFilename: string,
) => {
  const res = await client.get("/v1/analytics/export", {
    params,
    responseType: "blob",
  });
  const disposition: string | undefined = res.headers["content-disposition"];
  const filename =
    disposition?.match(/filename="([^"]+)"/)?.[1] ?? fallbackFilename;
  saveBlobAsFile(res.data, filename);
};

// Hands a Blob to the browser as a named download.
export const saveBlobAsFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

// Calendar bounds of an explicit "YYYY-MM to YYYY-MM" range, the custom-range
// half of trendMonthsStamp below (mirrors the server's getCustomMonthRange).
// Kept for SalesTrendPage.tsx, the one report whose custom range stays
// month-precision (see getYtdMonthRange's doc comment in dayjs.ts).
const customMonthStamp = (customRange: { from: string; to: string }) => {
  const start = tzDate(`${customRange.from}-01`);
  const end = tzDate(`${customRange.to}-01`).endOf("month");
  return `${start.format("YYYYMMDD")}-${end.format("YYYYMMDD")}`;
};

// Exact bounds of a day-precision "YYYY-MM-DD to YYYY-MM-DD" range, the
// custom-range half of analyticsRangeStamp below (mirrors the server's
// getCustomDayRange, including its stop at today for a range that has
// already started).
export const customDayStamp = (customRange: { from: string; to: string }) => {
  const start = tzDate(customRange.from);
  const requestedEnd = tzDate(customRange.to);
  const today = now();
  const end =
    requestedEnd.isAfter(today, "day") && !start.isAfter(today, "day")
      ? today
      : requestedEnd;
  return `${start.format("YYYYMMDD")}-${end.format("YYYYMMDD")}`;
};

// ASCII period stamp for export filenames, mirroring the server's rules
// (spott-<slug>-<YYYYMMDD>-<YYYYMMDD>): custom day range = exact bounds,
// otherwise the trailing window ending today (Jakarta).
//
// The window math MUST match getRollingWindowRange on the server, or the
// filename would name a different period than the workbook's own "Periode:"
// line: day presets are inclusive of today (7 = today plus the 6 before),
// while the month presets step back by calendar months (1m on 4 Aug = 4 Jul)
// and the year preset by one calendar year (1y on 14 Oct 2026 = 14 Oct 2025).
export const analyticsRangeStamp = (
  range: AnalyticsRange,
  customRange: { from: string; to: string } | null,
) => {
  if (customRange) return customDayStamp(customRange);
  const today = now();
  const start =
    range === "1y"
      ? today.subtract(1, "year")
      : range.endsWith("m")
        ? today.subtract(Number(range.slice(0, -1)), "month")
        : today.subtract(Number(range) - 1, "day");
  return `${start.format("YYYYMMDD")}-${today.format("YYYYMMDD")}`;
};

// Threshold reports (inactivity, expiring soon) have no date window; the
// stamp carries the threshold plus today instead.
export const thresholdStamp = (days: number) => `${days}hari-${now().format("YYYYMMDD")}`;

// Same idea for the one report whose threshold is credits, not days
// (mirrors the server's creditThresholdStamp).
export const creditThresholdStamp = (credits: number) =>
  `${credits}kredit-${now().format("YYYYMMDD")}`;

// Same idea for the trend report's calendar-month presets. `months` accepts
// "ytd" only in type (SalesTrendMonths) — selecting YTD always sets a
// non-null customRange too (see SalesTrendPage.tsx), so the arithmetic below
// never actually runs on a non-numeric months value; the early return
// handles it first.
export const trendMonthsStamp = (
  months: SalesTrendMonths,
  customRange: { from: string; to: string } | null,
) => {
  if (customRange) return customMonthStamp(customRange);
  const today = now();
  return `${today.startOf("month").subtract((months as number) - 1, "month").format("YYYYMMDD")}-${today.format("YYYYMMDD")}`;
};
